import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { CONDO_SESSION_KEY, CONDO_UNITS_SESSION_KEY_LABEL, Routes, QUOTE_LABEL, DETAIL_TYPE_STATIC, DETAIL_TYPE_PARAM_LABEL, CROSS_SELL_AT_CHECKOUT } from '@share/constants';
import {
  condoBookingResetState,
  getCondoReviewBook,
  ICondoReviewBookState,
  setCondoExpiredSession,
  setLoadingCondoBook,
} from '@store/slices';
import { setStripeError as setCondoStripeError } from '@store/slices/condo-review-book';
import { ICondosStaticState } from '@share/store/slices';
import { BookingErrorsEnum, ISessionKey } from '@share/common-types';
import { getAvailabilityId, getCondoId } from '@utils';
import {
  getSessionObj,
  getTimeout,
  ICertificateRouteHint,
  mergeCondoCertificateRouteHint,  
  UrlUtils,
  RootState,
  runStripeExpressResumeFromQuery,
  mergeStripeExpressResumeSearch,
  computeCreditLineChargeTotal,
  getSelectedCurrency,
  isCreditLineCheckoutAccount,
  redirectToCheckoutFromStripeResume,
  consumeStripeResumeErrorFromLocation,
} from '@share/utils';
import { store } from '@store';
import { CreditLineCheckoutPageBanner, StripeResumeError } from '@share/components';
import { IInsuranceState, InsuranceSelection } from '@store/slices';
import { Notification, SkeletonReviewBook, CrossSellSection } from '@components';
import { CondoReviewBook } from '../condo-review-book';
import { CondoUnitInfo } from '../condo-unit-info';
import { ICondoFiltersState, ILoginState } from '@share/store/slices';
import { NULL_VALUE } from '@constants';

import './style.scss';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface IMapStateToProps {
  condoReviewBookStore: ICondoReviewBookState;
  condosStaticStore: ICondosStaticState;
  condoFiltersStore: ICondoFiltersState;
  loginStore: ILoginState;
  insuranceStore: IInsuranceState;
}

interface IMapDispatchToProps {
  condoBookingResetState: () => void;
  getCondoReviewBook: (
    condoId: number,
    availabilityId: string,
    sessionKey: ISessionKey,
    isGetCoupon?: false,
    quote?: string,
    certificateRoute?: ICertificateRouteHint,
  ) => void;
  setLoadingCondoBook: (loadingBook: boolean) => void;
  setCondoExpiredSession: (expiredSession: boolean) => void;
  setCondoStripeError: (message: string) => void;
}

interface IState {
  detailType: string | null;
  stripeResumeError: string | null;
  stripeNoResume: boolean;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, RouteComponentProps {
  isStripe?: boolean;
}

const ZERO = 0;
class CondoReviewBookWrapperComponent extends React.Component<IProps, IState> {

  state: IState = { detailType: null, stripeResumeError: null, stripeNoResume: false };

  onExpiredSession = (sessionData: ISessionKey) => {
    const expireDate = sessionData.expireDate;

    if (getTimeout(expireDate) < ZERO) {
      this.props.setCondoExpiredSession(true);
    } else {
      this.setState({
        // @ts-ignore
        expirationTimeout: setTimeout(() => {
          this.props.setCondoExpiredSession(true);
        }, getTimeout(expireDate)),
      });
    }
  };

  getSessionId = () => {
    const values = UrlUtils.getValues();
    return getSessionObj(
      values[CONDO_UNITS_SESSION_KEY_LABEL] as ISessionKey,
      values[CONDO_SESSION_KEY] as ISessionKey,
    );
  }

  getCertificateRouteHint = (): ICertificateRouteHint | undefined => {
    return mergeCondoCertificateRouteHint(
      this.props.history.location.state,
      this.props.condoFiltersStore,
    );
  };

  getCertificateIdFromState = (): string | undefined => {
    return this.getCertificateRouteHint()?.certificateId;
  };

  componentWillUnmount() {
    this.props.condoBookingResetState();
  }

  componentDidMount() {
    const values = UrlUtils.getValues();
    const sessionKey = this.getSessionId();

    if (values[DETAIL_TYPE_PARAM_LABEL]) {
      this.setState({ detailType: (values[DETAIL_TYPE_PARAM_LABEL] as string) });
    }

    if (this.props.isStripe) {
      this.runStripeResume(sessionKey);
    } else {
      this.initNonStripe();
    }
    this.onExpiredSession(sessionKey);
    window.scrollTo(ZERO, ZERO);
  }

  /**
   * The `/unit-book/:id` and `/stripe/condo/:id` routes both mount `CondoReviewBookPage`, so React
   * Router reuses the same instance when we redirect from the Stripe page back to the checkout
   * page after a failed book-resume-express call. `componentDidMount` does not re-run on a prop
   * change, which would leave us with a blank page (no `condoBookingSummary` is fetched and the
   * inner `CondoReviewBook` is gated by it). Re-run the non-stripe init on the isStripe→false
   * transition so the checkout page renders with the resume error surfaced inline.
   */
  componentDidUpdate(prevProps: Readonly<IProps>) {
    const { history, loginStore, condoReviewBookStore } = this.props;
    const { loading, error } = condoReviewBookStore;
    const { account } = loginStore;
    const condoId = getCondoId();

    if (prevProps.isStripe && !this.props.isStripe) {
      // Component instance is being reused — wipe any state left behind by the failed Stripe
      // resume before kicking off the non-stripe init (componentWillUnmount didn't fire).
      this.props.condoBookingResetState();
      this.initNonStripe();
    }

    if (
      prevProps.condoReviewBookStore.loading !== loading &&
      !loading &&
      error === BookingErrorsEnum.BookingSummeryInformationFailed
    ) {
      history.push(`/${account?.name}${Routes.Condo}/${condoId}${location.search}`);
      location.reload();
    }
  }

  private runStripeResume(sessionKey: ISessionKey) {
    const resumeSearch = mergeStripeExpressResumeSearch(
      this.props.history.location.search,
      sessionKey,
    );
    void runStripeExpressResumeFromQuery({
      dispatch: store.dispatch,
      getState: store.getState as () => RootState,
      history: this.props.history,
      pathname: this.props.history.location.pathname,
      search: resumeSearch,
      vertical: 'condo',
    }).then((res) => {
      if (res.error && res.isResumeError) {
        // Resume call itself failed → hand off to the regular checkout page (which has the full
        // booking context) and let it surface the error inline as a normal checkout error. The
        // checkout page will pick up the redirected error in `initNonStripe()`.
        redirectToCheckoutFromStripeResume(this.props.history, res.error);
      } else if (res.error) {
        // Pre-resume failure (e.g., missing session) — show the full-page StripeResumeError.
        this.setState({ stripeResumeError: res.error });
      } else if (!res.ran) {
        // No bookingId in the URL (e.g., F5 after a successful booking, once params were stripped).
        this.setState({ stripeNoResume: true });
      }
    });
  }

  private initNonStripe() {
    const values = UrlUtils.getValues();
    const sessionKey = this.getSessionId();
    const quote = values[QUOTE_LABEL] ? (values[QUOTE_LABEL] as string) : undefined;

    // Surface a resume error redirected here from the /stripe/condo/... page (if any) so the
    // existing inline failure warning (getBookingErrorWarning) picks it up via `errorStripe`.
    const stripeResumeErrorMsg = consumeStripeResumeErrorFromLocation(this.props.history);
    if (stripeResumeErrorMsg) {
      this.props.setCondoStripeError(stripeResumeErrorMsg);
    }
    this.props.getCondoReviewBook(getCondoId(), getAvailabilityId(), sessionKey, false, quote, this.getCertificateRouteHint());
  }

  render(): React.ReactNode {
    if (this.props.isStripe && this.state.stripeResumeError) {
      return (
        <div className="review-book-wrapper print">
          <StripeResumeError />
        </div>
      );
    }

    if (this.props.isStripe && this.state.stripeNoResume) {
      return (
        <div className="review-book-wrapper print">
          <StripeResumeError mode="go-to-search" />
        </div>
      );
    }

    const { condoReviewBookStore, condosStaticStore, insuranceStore, loginStore } = this.props;
    const { detailType } = this.state;
    const {
      condoBookingSummary,
      loading,
      expiredSession,
      isExternalError,
      bookingComplete,
      isBookingPending,
      bookingErrorCode,
      loadingBooking,
      isSavingsMismatch,
      selectedCondoReviewClientCash,
      rewardId,
    } = condoReviewBookStore;
    const { condosCountriesCode } = condosStaticStore;
    const { account, userWalletData } = loginStore;
    const { selection, insurance } = insuranceStore;
    const condoId = getCondoId();
    const isNotification =
      bookingComplete ||
      loadingBooking ||
      isBookingPending ||
      bookingErrorCode === BookingErrorsEnum.SoldOut;

    const isUnavailable = bookingErrorCode === BookingErrorsEnum.SoldOut || bookingErrorCode === BookingErrorsEnum.RoomsUnavailable;
    
    const isStaticDetail = detailType === DETAIL_TYPE_STATIC;
    const condoName = condoBookingSummary?.bookingCard?.name?.replace(/\s+/g, '-').toLowerCase().replace(/\.|\,/g, '');

    const sessionKey = this.getSessionId();

    const { payNowPrice } = condoBookingSummary?.bookingCard?.bookingPrice ?? {};
    const { maxRewardWalletAmount } = condoBookingSummary?.bookingCard?.bookingPrice ?? {};
    const convertionRate = userWalletData?.convertionRate ?? 1;
    const insuranceAmount =
      selection === InsuranceSelection.YES && insurance?.productDetails?.price
        ? insurance.productDetails.price
        : 0;
    const rewardDeduction = rewardId && maxRewardWalletAmount ? maxRewardWalletAmount : 0;
    const creditLineBookingAmount =
      payNowPrice != null
        ? computeCreditLineChargeTotal({
            payOrBalance: payNowPrice,
            clientCashPoints: selectedCondoReviewClientCash,
            conversionRate: convertionRate,
            insuranceAmount,
            rewardDeduction,
          })
        : 0;
    const showCreditLineBanner =
      isCreditLineCheckoutAccount(account) &&
      !isNotification &&
      !!condoBookingSummary &&
      !loading &&
      !isUnavailable;

    /** On stripe-return pages, the wrapper has no booking data yet, so render a skeleton until the
     * resume settles (success → BookingConfirmed; failure → early-returned `<StripeResumeError />`). */
    const showStripeSkeleton = !!this.props.isStripe && !bookingComplete;

    return expiredSession || isExternalError || !condosCountriesCode? (
      <Notification
        expiredSession={expiredSession}
        isExternalError={isExternalError || !condosCountriesCode}
        isFromCondo={true}
        isSavingsMismatch={isSavingsMismatch}
        errorCode={isUnavailable ? bookingErrorCode : NULL_VALUE}
        sessionId={sessionKey?.value || ''}
      />
    ) : (
        <div className="review-book-wrapper print">
        {!isNotification && !this.props.isStripe && (
          <div className="review-book-wrapper__header print">
            <div className="review-book-wrapper__back-link">
              <Link to={`/${account?.name}${Routes.Condo}/${condoId}${(isStaticDetail) ? `/${condoName}`: ''}${location.search}`}>
                <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '5px' }} />
                <FormattedMessage id="back.to.condo" />
              </Link>
            </div>
            {!isUnavailable && (  
              <>
                <h4 className="review-book-wrapper__title secondary-font">
                  <FormattedMessage id="review.book" />
                </h4>
                <p className="review-book-wrapper__description">
                  <FormattedMessage id="enter.your.details" />
                </p>
              </>)}
          </div>)}

        {showCreditLineBanner ? (
          <CreditLineCheckoutPageBanner
            bookingAmount={creditLineBookingAmount}
            currency={getSelectedCurrency(account)}
          />
        ) : null}
        
        {(loading || showStripeSkeleton) ? <SkeletonReviewBook /> : null}

        {/* On stripe-return pages we only render the inner CondoReviewBook once the booking
            confirmed successfully — otherwise the form (and its inline error warning) would briefly
            flash before the resume-failure redirect navigates away (force-full-stripe flows can
            leave a `condoBookingSummary` in the store even when the resume call errored out). */}
        {(this.props.isStripe ? bookingComplete : !!condoBookingSummary) &&
        !loading &&
        condosCountriesCode &&
        !isUnavailable ? (
          <div className="review-book-wrapper__content">
            <CondoReviewBook />
            <CondoUnitInfo certificateId={this.getCertificateIdFromState()} />
          </div>
        ) : null}

        {/* The section belongs on the confirmation page. `CROSS_SELL_AT_CHECKOUT` also renders
            it before completion so it can be exercised without paying; off in every env. */}
        {(bookingComplete || (CROSS_SELL_AT_CHECKOUT && !!condoBookingSummary)) && (
          <CrossSellSection
            origin="condo"
            propertyId={condoId}
            checkIn={condoBookingSummary?.bookingCard?.checkIn}
            checkOut={condoBookingSummary?.bookingCard?.checkOut}
            currency={getSelectedCurrency(account)}
          />
        )}

        {isUnavailable && (
          <div className="review-book-wrapper__availability-error">
            <Notification
              expiredSession={expiredSession}
              isExternalError={isExternalError || !condosCountriesCode}
              isFromCondo={true}
              isSavingsMismatch={isSavingsMismatch}
              errorCode={isUnavailable ? bookingErrorCode : NULL_VALUE}
              sessionId={sessionKey?.value || ''}
            />
          </div>)}
        </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    condoReviewBookStore: state.condoReviewBookStore,
    condosStaticStore: state.condosStaticStore,
    condoFiltersStore: state.condoFiltersStore,
    loginStore: state.loginStore,
    insuranceStore: state.insuranceStore,
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  getCondoReviewBook,
  setLoadingCondoBook,
  setCondoExpiredSession,
  condoBookingResetState,
  setCondoStripeError,
};

export const CondoReviewBookWrapper = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(CondoReviewBookWrapperComponent));
